import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'Packages', href: '#packages' },
    { name: 'Services', href: '#services' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-gradient-to-b from-black/50 to-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Red Heart Symbol */}
          <div className="flex items-center space-x-3">
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                {/* Red Heart Logo */}
                <div className="relative">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    {/* Heart */}
                    <path
                      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      className={`transition-all duration-300 ${
                        isScrolled ? 'fill-red-600 stroke-red-600' : 'fill-red-500 stroke-red-500'
                      }`}
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                
                {/* Brand Text */}
                <div className="ml-3 flex flex-col">
                  <span className={`text-xl md:text-2xl font-bold font-serif leading-tight ${
                    isScrolled ? 'text-amber-800' : 'text-white'
                  }`}>
                    Jeevan Utsav Retreat
                  </span>
                </div>
              </div>
              
              {/* Tagline Below Logo */}
              <div className="mt-1 w-full">
                <span className={`text-xs tracking-wider uppercase block text-center ${
                  isScrolled ? 'text-gray-600' : 'text-amber-200'
                }`}>
                  Destination Weddings • Rishikesh
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation - Unique Design */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 group ${
                  isScrolled 
                    ? 'text-gray-900' 
                    : 'text-white'
                }`}
              >
                {/* Link text */}
                <span className="relative z-10">{link.name}</span>
                
                {/* Animated underline */}
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                  isScrolled ? 'bg-amber-700' : 'bg-amber-400'
                }`}></span>
                
                {/* Hover background */}
                <span className={`absolute inset-0 rounded-lg transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                  isScrolled ? 'bg-amber-50' : 'bg-white/10'
                }`}></span>
                
                {/* Active dot indicator */}
                <span className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  isScrolled ? 'bg-amber-700' : 'bg-amber-400'
                }`}></span>
              </a>
            ))}
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+919873514657"
              className={`flex items-center space-x-2 text-sm transition-colors px-3 py-2 rounded-lg ${
                isScrolled
                  ? 'text-gray-900 hover:bg-amber-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <Phone size={16} />
              <span className="font-medium">+91 98735 14657</span>
            </a>
            <Button
              className="bg-amber-700 hover:bg-amber-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              onClick={(e) => scrollToSection(e, '#contact')}
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`lg:hidden mt-4 pb-4 border-t pt-4 ${
            isScrolled ? 'border-gray-200 bg-white' : 'border-white/20 bg-black/30 backdrop-blur-md'
          }`}>
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={`text-sm font-medium transition-colors py-2 px-3 rounded-lg ${
                    isScrolled
                      ? 'text-gray-900 hover:bg-amber-50'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="tel:+919873514657"
                className={`flex items-center space-x-2 text-sm transition-colors py-2 px-3 rounded-lg ${
                  isScrolled
                    ? 'text-gray-900 hover:bg-amber-50'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <Phone size={16} />
                <span>+91 98735 14657</span>
              </a>
              <Button
                className="bg-amber-700 hover:bg-amber-800 text-white w-full"
                onClick={(e) => scrollToSection(e, '#contact')}
              >
                Get Quote
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
